import { ArrowLeft } from "lucide-react";
import * as React from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  useProjectClustersQuery,
  useProjectDetailQuery,
} from "@/entities/projects/queries";
import { useProjectStream } from "@/shared/hooks/useProjectStream";
import { Skeleton } from "@/shared/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { AnalysisTab } from "@/widgets/ProjectPage/AnalysisTab";
import { ClustersTab } from "@/widgets/ProjectPage/ClustersTab";
import { CollectLogSheet } from "@/widgets/ProjectPage/CollectLogSheet";
import { CollectTab } from "@/widgets/ProjectPage/CollectTab";
import { ProjectHeader } from "@/widgets/ProjectPage/ProjectHeader";

const TERMINAL = new Set(["done", "partial", "failed", "cancelled"]);
const ACTIVE = new Set(["queued", "collecting", "clustering"]);

export function ProjectPage(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const detailQ = useProjectDetailQuery(id, {
    refetchInterval: (q) => {
      // Пока активный — поллим деталь каждые 5с (для requests_used/cost_rub).
      const d = q.state.data;
      const status = d?.project?.status;
      return status && !TERMINAL.has(status) ? 5_000 : false;
    },
  });
  // Cluster-count для diff'а после расширения. Берём только когда статус
  // терминальный (чтобы не сравнивать с временно пустым набором).
  const clustersQ = useProjectClustersQuery(id, {}, true);
  const currentClustersCount = clustersQ.data?.items.length ?? 0;

  // Помним кол-во кластеров до начала активной фазы (= перед expand).
  const clustersBeforeRef = React.useRef<number | null>(null);
  const project = detailQ.data?.project;
  const isActive = project ? ACTIVE.has(project.status) : false;

  // SSE-стрим — нужен только пока проект активен. Передаём флаг в хук,
  // чтобы он реконнектился после «Расширить» (когда done→queued).
  const stream = useProjectStream(id, isActive);

  React.useEffect(() => {
    if (!project) return;
    if (isActive && clustersBeforeRef.current === null) {
      // Только что началась активная фаза — запоминаем «до».
      clustersBeforeRef.current = currentClustersCount;
    }
    if (!isActive) {
      // Терминальный статус — сбрасываем точку отсчёта.
      clustersBeforeRef.current = null;
    }
  }, [isActive, project, currentClustersCount]);

  // Когда SSE сигналит `done` — рефрешим деталь и кластеры, показываем diff.
  React.useEffect(() => {
    if (!stream.done) return;
    detailQ.refetch();
    clustersQ.refetch().then(() => {
      const before = clustersBeforeRef.current;
      if (before !== null && before > 0) {
        // Это был expand, не первичный сбор.
        const after = clustersQ.data?.items.length ?? 0;
        const diff = after - before;
        if (diff > 0) {
          toast.success(
            `Расширение завершено: было ${before} → стало ${after} (+${diff})`,
          );
        } else if (diff < 0) {
          toast.success(
            `Пересборка завершена: ${after} кластеров (раньше было ${before})`,
          );
        } else {
          toast.success(`Расширение завершено: те же ${after} кластеров`);
        }
        clustersBeforeRef.current = null;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream.done]);

  // --- табы ---
  // Активный таб синхронизируется со статусом проекта.
  const [activeTab, setActiveTab] = React.useState<string>("collect");
  const projectReady =
    !!project &&
    (project.status === "done" || project.status === "partial");

  React.useEffect(() => {
    if (!project) return;
    // Активная фаза → авто-перевод на «Сбор» (других данных нет).
    if (ACTIVE.has(project.status)) {
      if (activeTab !== "collect") setActiveTab("collect");
      return;
    }
    // Терминальная фаза → если юзер сидел на «Сбор» (потому что был активен) —
    // переключаем на Кластеры. Если выбрал какой-то другой таб руками — оставляем.
    if (projectReady && activeTab === "collect") {
      setActiveTab("clusters");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.status]);

  // --- лог в Sheet ---
  const [logOpen, setLogOpen] = React.useState(false);

  if (detailQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data || !project) {
    return (
      <div className="space-y-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" /> К проектам
        </Link>
        <div className="glass-card p-6 text-sm text-pink">
          Проект не найден или нет доступа.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> К проектам
      </Link>

      <ProjectHeader
        project={project}
        clustersCount={currentClustersCount}
        onOpenLog={() => setLogOpen(true)}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex w-full flex-wrap justify-start gap-1 overflow-x-auto bg-white/[0.04] p-1">
          {isActive && !projectReady && (
            <TabsTrigger value="collect">Сбор</TabsTrigger>
          )}
          {projectReady && (
            <TabsTrigger value="clusters">Кластеры</TabsTrigger>
          )}
          {projectReady && (
            <>
              <TabsTrigger value="video">🎬 Видео</TabsTrigger>
              <TabsTrigger value="posts">📝 Посты</TabsTrigger>
              <TabsTrigger value="lead_magnets">🎁 Лидмагниты</TabsTrigger>
              <TabsTrigger value="full">📊 Полный</TabsTrigger>
            </>
          )}
          {/* failed/cancelled — табов нет, только лог через ⋮ меню */}
        </TabsList>

        {isActive && !projectReady && (
          <TabsContent value="collect" className="mt-6">
            <CollectTab
              project={project}
              liveStatus={stream.status}
              liveProgress={stream.progress}
              liveCurrentStep={stream.currentStep}
              liveLogs={stream.logs}
            />
          </TabsContent>
        )}

        {projectReady && (
          <TabsContent value="clusters" className="mt-6">
            <ClustersTab projectId={project.id} />
          </TabsContent>
        )}

        {projectReady &&
          (["video", "posts", "lead_magnets", "full"] as const).map((t) => (
            <TabsContent key={t} value={t} className="mt-6">
              <AnalysisTab project={project} type={t} />
            </TabsContent>
          ))}

        {!isActive && !projectReady && (
          <div className="mt-6 glass-card p-6 text-sm text-muted-foreground">
            Сбор не завершён —{" "}
            <button
              type="button"
              onClick={() => setLogOpen(true)}
              className="text-cyan underline hover:no-underline"
            >
              открой журнал
            </button>
            , там подробности.
          </div>
        )}
      </Tabs>

      <CollectLogSheet
        open={logOpen}
        onOpenChange={setLogOpen}
        project={project}
        liveStatus={stream.status}
        liveProgress={stream.progress}
        liveCurrentStep={stream.currentStep}
        liveLogs={stream.logs}
      />
    </div>
  );
}
